import { useState, type ReactElement } from 'react';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EditPolicyDialog } from '@/components/EditPolicyDialog';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useFeatureFlagsApi } from '@/hooks/api/useFeatureFlagsApi';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import type { PolicyEditScope } from '@/hooks/api/usePolicyEditContext';
import { cn } from '@/lib/utils';
import { PolicyKey } from '@kirin/api-client/contracts/policies';

export const EDIT_POLICY_BUTTON_WIDTH_CLASS = 'min-w-[6.25rem] justify-center';

/** Sub-policy keys backed by Allowed/Blocked lists (policy-exceptions API). */
const LIST_BASED_SUB_POLICIES = new Set([
  'extensionsBlockedList',
  'mcpServersBlockedList',
  'mcpServersUntrustedSource',
]);

/**
 * Sub-policy keys with no policy mode to edit, and where the alert is acted
 * on instead (KIR-4482: a New MCP server is allowed or blocked from
 * Inventory).
 */
const ACTED_ON_ELSEWHERE: Readonly<Record<string, string>> = {
  mcpServersNewServer:
    'Block or allow this server from Inventory → MCP Servers',
};

interface AlertEditPolicyButtonProps {
  scope: PolicyEditScope;
  policyKey: string | undefined;
  subPolicyKey?: string | null;
  alertKey: string;
  className?: string;
  /**
   * KIR-4700: renders the trigger as an icon-only square button (no visible
   * "Edit Policy" / "View Policy" label) for the condensed alerts-table
   * Actions column. The tooltip still carries the label.
   */
  iconOnly?: boolean;
}

/**
 * Button shown in alert row actions that opens an inline policy editor dialog.
 *
 * Visibility comes from the role, the enabled state from the
 * `edit_policy_from_alerts` flag:
 * - member (org scope) → nothing rendered
 * - support → "View Policy"; `EditPolicyDialog` disables Save/Reset
 * - owner / manager → "Edit Policy"
 * - flag off (org scope) → the trigger still renders, but disabled
 *
 * For organization scope the flag is read from the **backend**
 * (`useFeatureFlagsApi` with the org id) rather than the browser Flagsmith
 * SDK. The SDK evaluates the signed-in user's identity, which can report "on"
 * for a user whose organization has the flag off; the dialog then opened onto
 * a 404 from `GET …/policies/:policyKey/edit-context` and rendered "Failed to
 * load policy context" (KIR-4096). Account scope edits the user's own
 * policies, has no org to evaluate against, and keeps the SDK path.
 */
export function AlertEditPolicyButton({
  scope,
  policyKey,
  subPolicyKey,
  alertKey,
  className,
  iconOnly,
}: AlertEditPolicyButtonProps): ReactElement | null {
  const orgScopeId =
    scope.kind === 'organization' ? scope.organizationId : undefined;

  // Org scope asks for no keys at all: the SDK's answer is not merely unused
  // there, it is the bug (KIR-4096). Requesting it anyway would leave a value
  // sitting in scope for the next reader to wire up, and makes the SDK
  // evaluate a flag whose result is thrown away.
  const clientFlags = useFeatureFlags(
    orgScopeId ? [] : [flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS]
  );
  const serverFlags = useFeatureFlagsApi(
    [flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS],
    !!orgScopeId,
    orgScopeId
  );
  const writeGate = useOrgWriteGate(orgScopeId);

  const [dialogOpen, setDialogOpen] = useState(false);

  const flagEnabled = orgScopeId
    ? (serverFlags.data?.[flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS] ??
      false)
    : (clientFlags.flags[flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS] ??
      false);
  const flagsLoading = orgScopeId
    ? serverFlags.isLoading
    : clientFlags.isLoading;

  if (flagsLoading) {
    return null;
  }
  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  } else if (!flagEnabled) {
    // Account scope has no org-level rollout to disable against, so keep the
    // existing hide-when-off behaviour there.
    return null;
  }

  const available = !!policyKey;
  // KIR-4443: the package Block list is fixed at Enforce and managed from the
  // Packages page, so there is no policy mode here to edit.
  const isListBased =
    policyKey === PolicyKey.PackagesManagement ||
    (!!subPolicyKey && LIST_BASED_SUB_POLICIES.has(subPolicyKey));
  const actedOnElsewhere = subPolicyKey
    ? ACTED_ON_ELSEWHERE[subPolicyKey]
    : undefined;
  const canOpenDialog =
    flagEnabled && available && !isListBased && !actedOnElsewhere;
  const isDisabled = !canOpenDialog;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          {/*
           * Radix `TooltipTrigger asChild` on a disabled <button> receives no
           * pointer events, so the tooltip never appears on hover. Wrap in a
           * focusable span so the trigger picks up hover/focus even when the
           * button underneath is disabled.
           */}
          <span
            tabIndex={isDisabled ? 0 : -1}
            className={cn('inline-flex', isDisabled && 'cursor-not-allowed')}
          >
            <Button
              variant="outline"
              size={iconOnly ? 'icon-sm' : 'sm'}
              className={cn(
                !iconOnly && EDIT_POLICY_BUTTON_WIDTH_CLASS,
                isDisabled && 'pointer-events-none',
                className
              )}
              onClick={() => setDialogOpen(true)}
              disabled={isDisabled}
              // The visible label carries the accessible name when it is
              // rendered; `iconOnly` (KIR-4700) removes that text, so the
              // aria-label picks up the same copy to keep the button
              // announced correctly for screen readers either way.
              aria-label={writeGate.disabled ? 'View Policy' : 'Edit Policy'}
            >
              <Settings2
                className={iconOnly ? 'h-3.5 w-3.5' : 'mr-1 h-3.5 w-3.5'}
              />
              {!iconOnly &&
                (writeGate.disabled ? 'View Policy' : 'Edit Policy')}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {!flagEnabled
            ? 'Not enabled for this organization'
            : actedOnElsewhere
              ? actedOnElsewhere
              : isDisabled
                ? 'Not Available'
                : writeGate.disabled
                  ? 'View the policy that generated this alert'
                  : 'Edit the policy that generated this alert'}
        </TooltipContent>
      </Tooltip>

      {canOpenDialog && (
        <EditPolicyDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          scope={scope}
          policyKey={policyKey}
          subPolicyKey={subPolicyKey ?? undefined}
          alertKey={alertKey}
        />
      )}
    </>
  );
}
